<script setup>
import { onMounted, computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";

import Filters from "@/components/admin/filters/Filters.vue";

import debounce from "lodash/debounce";
import { useAdminStore } from "@/stores";

const adminStore = useAdminStore();

const router = useRouter();

const data = computed(() => adminStore.overview);

const params = computed(() => adminStore.requestQueryParams);

const pagination = computed(() => adminStore.pagination);

const isFetching = ref(false);

const formatDateTime = (date) => {
  if (date === null) {
    return date;
  }
  return moment(date, "YYYY-MM-DDTHH:mm:ss").format("DD-MM-YYYY HH:mm");
};

const getUsers = async () => {
  isFetching.value = true;

  params.value.set("page", pagination.value.currentPage - 1);
  params.value.set("size", pagination.value.pageSize);

  await adminStore.getAdminUsersOverview(params.value);

  isFetching.value = false;
};

const checkClickEvent = (event) => {
  const check = event.target && event.target.classList.contains("mdi-pencil");
  return check;
};

const checkClickDeactivateEvent = (event) => {
  const check =
    event.target && event.target.classList.contains("mdi-account-cancel");
  return check;
};

const checkClickActivateEvent = (event) => {
  const check =
    event.target && event.target.classList.contains("mdi-account-cancel");
  return check;
};

const edit = (item, event) => {
  const check = checkClickEvent(event);
  if (check) {
    router.push({
      path: `/admin/user/edit/${item}`,
    });
  }
};

const handleActivate = (item) => {
  const payload = { id: item.id, active: true };
  const result = adminStore.activateUser(payload);
  if (result) {
    item.active = true;
  }
};

const handleDeactivate = (item) => {
  const payload = { id: item.id, active: false };
  adminStore.deactivateUser(payload);
};

const download = ref(false);

const exportTable = async () => {
  download.value = true;
  const resp = await adminStore.downloadExcel(params.value);

  const url = window.URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "users.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  download.value = false;
};

const coursesDebounced = debounce(getUsers, 500);

watch(
  pagination,
  () => {
    if (!isFetching.value) {
      getUsers();
    }
  },
  {
    deep: true,
  }
);

watch(
  params,
  () => {
    coursesDebounced();
  },
  {
    deep: true,
  }
);

const headers = [
  {
    title: "Actions",
    key: "actions",
    align: "center",
    sortable: false,
    fixed: true,
    width: "100px",
  },
  { title: "Email", key: "email", width: "300px" },
  { title: "Name", key: "name", width: "300px" },
  { title: "Responsibilities", key: "responsibilities", width: "300px" },
  { title: "Created date", key: "createdDate", width: "200px" },
  {
    title: "Active",
    key: "active",
    width: "100px",
    align: "center",
    sortable: false,
    fixed: true,
  },
];

// onBeforeMount(() => {
//   academyStore.resetAction();
// });

onMounted(() => {
  getUsers();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-10 page-title">Users Overview</h1>
        <Filters />
        <div class="d-flex justify-space-between align-center mb-5">
          <h3>Total: {{ pagination.totalItems }}</h3>
          <v-btn
            color="admin"
            :loading="download"
            :disabled="download"
            @click="exportTable()"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-file-excel</v-icon>
              <span>Export</span>
            </div>
          </v-btn>
        </div>
        <v-data-table
          :items="data"
          :headers="headers"
          :items-per-page="pagination.pageSize"
          :hide-default-footer="true"
          :loading="isFetching"
          class="courses-table bordered-columns"
        >
          <template #item.createdDate="{ item }">
            {{ formatDateTime(item.createdDate) }}
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                color="admin"
                @click="edit(item.id, $event)"
              />
            </div>
          </template>
          <template #item.active="{ item }">
            <GeneralPrompt
              icon="mdi-toggle-switch-off"
              color="error"
              size="large"
              text="User will be active again."
              v-if="item.active == false"
              @confirm="handleActivate(item)"
            />

            <GeneralPrompt
              icon="mdi-toggle-switch"
              color="success"
              size="large"
              text="User will be deactivated!"
              v-else
              @confirm="handleDeactivate(item)"
            />
          </template>
          <template #item.responsibilities="{ item }">
            <v-chip
              class="mt-2 mb-2"
              v-for="responsibility in item.responsibilities"
              :key="responsibility.value"
            >
              {{ responsibility.value }}
            </v-chip>
          </template>
        </v-data-table>
        <div class="mt-5">
          <v-pagination
            v-model="pagination.currentPage"
            :length="pagination.pageCount"
            :total-visible="5"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-admin));
}
.v-table {
  table-layout: fixed;

  td,
  th {
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    white-space: normal;
  }
}
</style>
